import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings -> Reports: capul de familie si programarile, calea noua.
 *
 * Perechile vechi:
 *
 *   settings/Company_reports::index              -> GET  /settings/company-reports
 *   settings/Company_reports::scheduled_report   -> GET  /settings/company-reports/schedules
 *   settings/Catalog::save                       -> POST /settings/company-reports/schedules
 *   settings/Catalog::remove                     -> POST .../schedules/{id}/remove
 *   settings/Catalog::sendNow                    -> POST .../schedules/{id}/send
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * BARA DE FILE vine FILTRATA DE PE SERVER, ca la Settings -> Companies: ecranul
 * nu stie ce drepturi are omul si nu ascunde nimic singur. Vin numai filele
 * ecranelor care CHIAR sunt in v4 - portalul vechi nu mai exista pe server,
 * deci o fila catre el ar fi o legatura moarta.
 */

/** O fila din bara ecranului. `route` e calea din v4, fara `/app`. */
export type CompanyReportTab = {
  id: string
  label: string
  icon: string
  route: string
}

export type CompanyReportsShell = {
  pageTitle: string
  pageDescription: string
  moduleUrl: string
  /** DEJA filtrate pe server. */
  tabs: CompanyReportTab[]
}

export const companyReportsShellQuery = queryOptions({
  queryKey: ['settings', 'company-reports', 'shell'],
  queryFn: () => apiGet<CompanyReportsShell>('/settings/company-reports'),
})

/* -------------------------------------------------------------------------
 * Scheduled Reports (views/version-3/settings/catalog/index.php + form.php)
 * ---------------------------------------------------------------------- */

export type NumberOption = { value: number; label: string }

export type TextOption = { value: string; label: string }

export type ScheduleCompany = { id: number; name: string }

/**
 * O programare, cu tot ce arata lista SI tot ce cere formularul.
 *
 * In portal, modala isi cerea randul inca o data (`Catalog::form/{id}`).
 * Lista aducea deja `SELECT s.*`, deci al doilea drum nu spunea nimic nou.
 */
export type ReportSchedule = {
  id: number
  name: string
  /** Cheia raportului: `service_costs`, ..., sau `custom:<ID>`. */
  report: string
  /** Cum se cheama raportul ala; cheia insasi cand nu se mai stie. */
  reportLabel: string
  /** 0 = orice client. */
  companyId: number
  companyName: string
  monthsBack: number
  spanLabel: string
  frequency: number
  dayOfMonth: number
  dayOfWeek: number
  hour: number
  /** Cand pleaca urmatoarea data, in cuvinte. Socotit, nu tinut in baza. */
  next: string
  addresses: string
  attachCsv: boolean
  /** 1 = pornita; 0 = oprita, si randul se deseneaza sters. */
  status: number
  /** Gol = n-a plecat niciodata. */
  lastSentAt: string
}

export type SchedulesPage = {
  pageTitle: string
  pageDescription: string
  schedules: ReportSchedule[]
  companies: ScheduleCompany[]
  frequencies: NumberOption[]
  weekdays: NumberOption[]
  spans: NumberOption[]
  reports: TextOption[]
  /** `inventoree.service_catalog.edit` - dreptul cu care portalul pazea scrierile. */
  canWrite: boolean
  /** Fara server de cronuri, "Send now" n-are pe cine sa cheme. */
  cronConfigured: boolean
}

export const reportSchedulesQuery = queryOptions({
  queryKey: ['settings', 'company-reports', 'schedules'],
  queryFn: () => apiGet<SchedulesPage>('/settings/company-reports/schedules'),
})

/** Fara `ID` (sau cu 0) scrie una noua; cu `ID` o rescrie pe a furnizorului. */
export function saveSchedule(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number; message: string }>('/settings/company-reports/schedules', fields)
}

export function removeSchedule(id: number) {
  return apiPost<{ removed: boolean; message: string }>(`/settings/company-reports/schedules/${id}/remove`, {})
}

/**
 * Trimite programarea ACUM, orice ar zice ceasul.
 *
 * Raspunde 200 si cu `sent: false`: o incercare care s-a lovit de altceva -
 * serverul de cronuri nesunat, perioada fara costuri, treaba deja pornita - nu
 * e o cerere gresita, iar motivul trebuie citit de om.
 */
export function sendScheduleNow(id: number) {
  return apiPost<{ sent: boolean; message: string }>(`/settings/company-reports/schedules/${id}/send`, {})
}

/* -------------------------------------------------------------------------
 * Users Reports (views/version-3/settings/company_reports/users_settings/**)
 *
 * Perechile vechi, toate din `settings/Company_reports`:
 *
 *   users_settings_report            -> GET  /settings/company-reports/users
 *   getUsersData                     -> GET  /settings/company-reports/users/table
 *   companyReportsSettings($userId)  -> GET  .../users/{id}/modules
 *   saveUserModules($userId)         -> POST .../users/{id}/modules
 *   otherReportsSettings($userId)    -> GET  .../users/{id}/reports
 *   saveUserReportsSettings($userId) -> POST .../users/{id}/reports
 *   multipleCompanyReportsSettings   -> modulele vin in GET .../users (`modules`)
 *   saveMultipleUsersModules         -> POST .../users/modules
 *
 * ORA: serverul da si primeste ora LOCALA a omului logat, `HH:MM`, cum o
 * scria si citea formularul vechi (mutarea in GMT se face pe server, ca in
 * portal).
 * ---------------------------------------------------------------------- */

/** Un modul cu raport, pentru formularul de grup. */
export type ReportModule = { id: number; display_name: string }

export type UsersReportsShell = {
  title: string
  moduleUrl: string
  /**
   * `ok` - lista; `disabled` - firma aleasa n-are rapoartele pornite
   * (avertismentul cu legatura spre Customers); `denied` - nu e furnizor.
   * HOTARAT PE SERVER, ca in macheta veche (`$_SESSION['access'] == 3 &&
   * $company->enableReporting == 1`).
   */
  state: 'ok' | 'disabled' | 'denied'
  companyId: number
  customersUrl: string
  modules: ReportModule[]
}

export const usersReportsShellQuery = queryOptions({
  queryKey: ['settings', 'company-reports', 'users', 'shell'],
  queryFn: () => apiGet<UsersReportsShell>('/settings/company-reports/users'),
})

/** Un rand din lista (`Summary::getTableData`): omul si bifele lui de email pe module. */
export type UsersReportRow = {
  ID: number
  Prenume: string
  Nume: string
  DT_RowId: string
  inventoree: number
  inventoree_email: number
  proactivitee: number
  proactivitee_email: number
  printeeng: number
  printeeng_email: number
  securitee: number
  securitee_email: number
  productivitee: number
  productivitee_email: number
}

export type UsersReportsTable = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: UsersReportRow[]
}

export type UsersTableRequest = {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderDir: 'asc' | 'desc'
  search: string
}

export function usersReportsTableQuery(r: UsersTableRequest) {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set('search[value]', r.search)
  return queryOptions({
    queryKey: ['settings', 'company-reports', 'users', 'table', r],
    queryFn: () => apiGet<UsersReportsTable>(`/settings/company-reports/users/table?${p.toString()}`),
  })
}

/** Un rand din `it_company_reports_user_modules`, cu numele modulului si ora locala. */
export type UserModuleRow = {
  module_id: number
  display_name: string
  view_report: number
  email_report: number
  daily: number
  weekly: number
  monthly: number
  enable_alarms: number
  /** `HH:MM` local, sau gol. */
  email_time: string
}

export type UserModules = { userId: number; modules: UserModuleRow[] }

export function userModulesQuery(userId: number) {
  return queryOptions({
    queryKey: ['settings', 'company-reports', 'users', userId, 'modules'],
    queryFn: () => apiGet<UserModules>(`/settings/company-reports/users/${userId}/modules`),
  })
}

/** Bifele unui modul, cum le trimitea formularul vechi (`module[<id>][...]`). */
export type ModuleForm = {
  view_report: boolean
  email_report: boolean
  daily: boolean
  weekly: boolean
  monthly: boolean
  enable_alarms: boolean
  email_time: string
}

/** `module[<id>][camp]=1` numai pentru bifele puse, ca la un formular adevarat. */
export function moduleFields(modules: Record<number, ModuleForm>): Record<string, PostValue> {
  const fields: Record<string, PostValue> = {}
  for (const [id, m] of Object.entries(modules)) {
    for (const flag of ['view_report', 'email_report', 'daily', 'weekly', 'monthly', 'enable_alarms'] as const) {
      if (m[flag]) fields[`module[${id}][${flag}]`] = '1'
    }
    fields[`module[${id}][email_time]`] = m.email_time
  }
  return fields
}

export function saveUserModules(userId: number, modules: Record<number, ModuleForm>) {
  return apiPost<{ saved: boolean; userId: number }>(`/settings/company-reports/users/${userId}/modules`, moduleFields(modules))
}

export function saveManyUsersModules(users: number[], modules: Record<number, ModuleForm>) {
  return apiPost<{ saved: boolean; users: number[] }>('/settings/company-reports/users/modules', {
    users,
    ...moduleFields(modules),
  })
}

/** Un raport din `it_reports`, cu setarea omului (sau ora de acum, cand n-are). */
export type UserReportRow = {
  id: number
  report_name: string
  set: boolean
  email: number
  email_time: string
}

export type UserReports = { userId: number; reports: UserReportRow[] }

export function userReportsQuery(userId: number) {
  return queryOptions({
    queryKey: ['settings', 'company-reports', 'users', userId, 'reports'],
    queryFn: () => apiGet<UserReports>(`/settings/company-reports/users/${userId}/reports`),
  })
}

export function saveUserReports(userId: number, reports: Record<number, { email: boolean; email_time: string }>) {
  const fields: Record<string, PostValue> = {}
  for (const [id, r] of Object.entries(reports)) {
    if (r.email) fields[`reports[${id}][email]`] = '1'
    fields[`reports[${id}][email_time]`] = r.email_time
  }
  return apiPost<{ saved: boolean; userId: number; count: number }>(`/settings/company-reports/users/${userId}/reports`, fields)
}
